import { Box, Stack, Typography } from '@mui/material';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { url } from '../../appRouter/url.ts';
import { ContentLoading, EasyBreadcrumbs, NoContentInfo, ThemaSummary } from '../../components/general';
import { AusschreibungRueckmeldungenCard } from '../../components/general/Ausschreibung/AusschreibungRueckmeldungenCard.tsx';
import { AusschreibungsprozessCard } from '../../components/general/Ausschreibung/AusschreibungsprozessCard.tsx';
import { isTerminvorschlaegePruefenEnabled } from '../../components/general/Ausschreibung/terminvorschlaegePruefenFeature.ts';
import { FailedQueryAlert } from '../../components/general/ErrorAlert.tsx';
import { StickyWrapperWithBlur } from '../../components/general/StickyWrapper.tsx';
import { AkaVeranstaltungId, GetDurchgangByAkaDurchgangIdOutput, Leistung, Veranstaltung } from '../../dtos.ts';
import { useNormalizedParams } from '../../hooks/useNormalizedParams.ts';
import { usePageTitle } from '../../hooks/usePageTitle.ts';
import { useThemaAndLinkedThemaQuery } from '../../hooks/useThemaAndLinkedThemaQuery.ts';
import { trpc } from '../../trpc.ts';
import { formatDate } from '../../utils/dateFormat/dateFormat.ts';
import { isAkaDurchgangId } from '../../utils/isAkaDurchgangId.ts';
import { isAkaVeranstaltungId } from '../../utils/isAkaVeranstaltungId.ts';
import { getAllLeistungen, getSortedModulesForLeistung } from '../../utils/teilleistung.ts';
import { VorschlaegePruefen } from '../VeranstaltungDetailsPage/VorschlaegePruefen.tsx';
import { DurchgangDetailsHeader } from './DurchgangDetailsHeader.tsx';
import { DurchgangOverview } from './DurchgangOverview.tsx';
import { DurchgangOverviewTabBar } from './DurchgangOverviewTabBar.tsx';
import { ModulterminDetails } from './ModulterminDetails.tsx';

export const DurchgangDetailsPage = () => {
  const { akaDurchgangId } = useNormalizedParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [pruefung, setPruefung] = useState<{
    leistungId: Leistung['leistungId'];
    teilleistungId: string;
  } | null>(null);

  const akaVeranstaltungId = location.hash ? location.hash.slice(1) : null;

  if (!isAkaDurchgangId(akaDurchgangId)) {
    throw new TypeError(`Die AkaDurchgangID fehlt in der URL oder ist ungültig: ${akaDurchgangId}`);
  }

  const durchgangQuery = trpc.durchgang.getDurchgangByAkaDurchgangId.useQuery({ akaDurchgangId });
  const durchgangQueryErrorCode = durchgangQuery.error?.data?.code;
  const durchgang = durchgangQuery.data;

  usePageTitle(getTitle(durchgang));

  const { thema, linkedThema, themaQueryError, themaQueryLoading } = useThemaAndLinkedThemaQuery(
    durchgang?.thema.akaProduktId,
  );

  if (akaVeranstaltungId !== null && !isAkaVeranstaltungId(akaVeranstaltungId)) {
    return <NoContentInfo content="Ungültige akaVeranstaltungId" />;
  } else if (durchgangQueryErrorCode === 'UNPROCESSABLE_CONTENT') {
    return <NoContentInfo content="Ungültige akaDurchgangId" />;
  } else if (durchgangQueryErrorCode && durchgangQueryErrorCode !== 'NOT_FOUND') {
    return <FailedQueryAlert error={durchgangQuery.error} query="Durchgang" />;
  } else if (themaQueryError && themaQueryError.data?.code !== 'NOT_FOUND') {
    return <FailedQueryAlert error={themaQueryError} query="Thema" />;
  } else if (themaQueryLoading || durchgangQuery.isLoading) {
    return <ContentLoading />;
  } else if (!thema) {
    return <NoContentInfo content="Thema zum Durchgang nicht gefunden" />;
  }

  if (!durchgang) {
    return (
      <NoContentInfo
        content={
          <Typography>
            Durchgang mit der akaDurchgangId &quot;{akaDurchgangId}&quot; nicht gefunden. Er wurde entweder gelöscht
            oder ist nicht in Viva-Planung enthalten.
          </Typography>
        }
      />
    );
  }

  const getModultermin = (): Veranstaltung | null => {
    if (akaVeranstaltungId === null) {
      return null;
    }

    return durchgang.modultermine.find((modultermin) => modultermin.akaVeranstaltungId === akaVeranstaltungId) ?? null;
  };

  const onTabChange = (akaVeranstaltungId: AkaVeranstaltungId | null) => {
    void navigate(`#${akaVeranstaltungId ?? ''}`, { replace: true });
  };

  const modultermin = getModultermin();
  if (akaVeranstaltungId && !modultermin) {
    return (
      <NoContentInfo
        content={`Der Modultermin mit der AkaVeranstaltungId "${akaVeranstaltungId}" ist nicht Teil des Durchgangs`}
      />
    );
  }

  const uniqueLeistungen = getAllLeistungen(
    durchgang.modultermine.map((modultermin) => modultermin.teilleistungen).flat(1),
  );
  const zuPruefenderModultermin = pruefung
    ? durchgang.modultermine.find((mt) =>
        mt.teilleistungen.some((teilleistung) => teilleistung.teilleistungId === pruefung.teilleistungId),
      )
    : undefined;
  const showManuellerAusschreibungsprozessInfo = durchgang.modultermine.some(
    (modultermin) => modultermin.manuellerAusschreibungsprozess,
  );

  return (
    <Stack sx={{ paddingTop: 2 }}>
      <EasyBreadcrumbs
        items={[
          { label: 'Dashboard', href: url.root() },
          { label: 'Thema', href: url.thema.details(durchgang.thema.akaProduktId) },
          { label: 'Durchgang', href: '' },
        ]}
      />
      <StickyWrapperWithBlur>
        <Box display="grid" gridTemplateColumns="1fr 320px" gap={3}>
          <DurchgangDetailsHeader durchgang={durchgang} />
          <ThemaSummary thema={thema} linkedThema={linkedThema} showThemaLink />
        </Box>
      </StickyWrapperWithBlur>
      <Stack spacing={3} mt={3}>
        <Stack spacing={3}>
          {uniqueLeistungen.map((leistung) => (
            <Box key={leistung.leistungId} display="grid" gridTemplateColumns={{ xs: '1fr', lg: '1fr 2fr' }} gap={2}>
              <AusschreibungsprozessCard
                leistung={leistung}
                showManuellerAusschreibungsprozessInfo={showManuellerAusschreibungsprozessInfo}
              />
              <AusschreibungRueckmeldungenCard
                leistung={leistung}
                teilleistungColumns={getSortedModulesForLeistung(durchgang.modultermine, leistung.leistungId)}
                onVorschlaegePruefen={
                  isTerminvorschlaegePruefenEnabled()
                    ? (teilleistungId) => {
                        setPruefung({ leistungId: leistung.leistungId, teilleistungId });
                      }
                    : undefined
                }
              />
            </Box>
          ))}
        </Stack>
      </Stack>
      <Box mt={7}>
        <Stack spacing={2}>
          <DurchgangOverviewTabBar
            modultermine={durchgang.modultermine}
            akaVeranstaltungId={akaVeranstaltungId}
            onTabChange={onTabChange}
          />
          {modultermin ? (
            <ModulterminDetails modultermin={modultermin} thema={thema} />
          ) : (
            <DurchgangOverview modultermine={durchgang.modultermine} thema={thema} />
          )}
        </Stack>
      </Box>
      {pruefung && zuPruefenderModultermin && (
        <VorschlaegePruefen
          thema={thema}
          veranstaltung={zuPruefenderModultermin}
          leistungId={pruefung.leistungId}
          activeTeilleistungId={pruefung.teilleistungId}
          modulLabel={`Modul ${String(zuPruefenderModultermin.modul.modulnummer)}`}
          onClose={() => {
            setPruefung(null);
          }}
        />
      )}
    </Stack>
  );
};

function getTitle(durchgang: GetDurchgangByAkaDurchgangIdOutput | undefined): string {
  if (!durchgang) {
    return 'Durchgang';
  }

  const buchungsnummer = durchgang.thema.buchungsnummer;
  return `${buchungsnummer} | ${formatDate(durchgang.zeitraum.start)} | Durchgang`;
}
